/**
 * A tool as an editor shows a function: its name, the inputs it takes and the fields it returns.
 * Pointing at an input or an output says what it is underneath; clicking one keeps it there.
 */

import { Fragment, useState } from 'react';
import { cn } from '@/lib/utils';
import { IntegrationLogo } from '@/modules/integrations';
import { humanize } from '@/modules/simulator/ui/text';
import type { ToolDefinitionAction } from '@/services/toolDefinition.service';
import { AccessBadge } from './tool-access';
import { schemaFields, toolKey, typeLabels, type ToolField } from './tool-schema';

type Side = 'input' | 'output';

interface Slot {
	id: string;
	side: Side;
	field: ToolField;
	type: string;
}

const slotsOf = (side: Side, schema: unknown): Slot[] => {
	const types = typeLabels(schema);
	return schemaFields(schema).map((field) => ({
		id: `${side}:${field.name}`,
		side,
		field,
		type: types[field.name] ?? 'any'
	}));
};

const Punct = ({ children }: { children: string }) => <span className='text-gray-800'>{children}</span>;

interface TokenProps {
	slot: Slot;
	active: boolean;
	pinned: boolean;
	onPoint: (id: string | null) => void;
	onPin: (id: string) => void;
}

const Token = ({ slot, active, pinned, onPoint, onPin }: TokenProps) => (
	<button
		type='button'
		aria-pressed={pinned}
		onMouseEnter={() => onPoint(slot.id)}
		onMouseLeave={() => onPoint(null)}
		onFocus={() => onPoint(slot.id)}
		onBlur={() => onPoint(null)}
		onClick={() => onPin(slot.id)}
		className={cn(
			'rounded-sm px-0.5 underline decoration-gray-500 decoration-dotted underline-offset-4 transition-colors',
			active ? 'bg-indigo-50 text-indigo-800' : 'text-text-body hover:bg-indigo-50',
			pinned && 'ring-1 ring-indigo-300'
		)}
	>
		{slot.field.name}
		{slot.side === 'input' && !slot.field.required && <span className='text-gray-800'>?</span>}
	</button>
);

const Inspector = ({ slot }: { slot: Slot | null }) => (
	<div aria-live='polite' className='flex min-h-16 flex-col gap-1 rounded-lg border border-gray-500 px-3 py-2'>
		{slot ? (
			<>
				<p className='flex flex-wrap items-baseline gap-x-1.5 text-xs leading-4'>
					<span className='font-mono font-medium text-text-heading'>{slot.field.name}</span>
					<span className='text-gray-800'>
						{slot.side === 'input'
							? `Input · ${slot.type} · ${slot.field.required ? 'required' : 'optional'}`
							: `Output · ${slot.type}`}
					</span>
				</p>
				<p className='text-xs leading-4 text-text-body'>{slot.field.description ?? 'No description.'}</p>
				{slot.field.options.length > 0 && (
					<p className='text-xs leading-4 text-gray-800'>One of {slot.field.options.join(', ')}</p>
				)}
			</>
		) : (
			<p className='text-xs leading-4 text-gray-800'>Point at an input or an output to see what it is; click to keep it here.</p>
		)}
	</div>
);

const ToolCard = ({ tool }: { tool: ToolDefinitionAction }) => {
	const [pointed, setPointed] = useState<string | null>(null);
	const [pinned, setPinned] = useState<string | null>(null);
	const inputs = slotsOf('input', tool.inputSchema);
	const outputs = slotsOf('output', tool.outputSchema);
	const activeId = pointed ?? pinned;
	const token = (slot: Slot) => (
		<Token
			slot={slot}
			active={slot.id === activeId}
			pinned={slot.id === pinned}
			onPoint={setPointed}
			onPin={(id) => setPinned((current) => (current === id ? null : id))}
		/>
	);
	// Each name stays joined to its brackets and comma, so a line only breaks after a comma.
	const list = (slots: Slot[], open: string, close: string) =>
		slots.length === 0 ? (
			<Punct>{`${open}${close}`}</Punct>
		) : (
			slots.map((slot, index) => (
				<Fragment key={slot.id}>
					<span className='whitespace-nowrap'>
						{index === 0 && <Punct>{open}</Punct>}
						{token(slot)}
						<Punct>{index === slots.length - 1 ? close : ','}</Punct>
					</span>
					{index < slots.length - 1 && ' '}
				</Fragment>
			))
		);

	return (
		<div className='flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto p-4'>
			<div className='flex flex-col gap-1'>
				<div className='flex min-w-0 items-center gap-2'>
					<IntegrationLogo provider={tool.provider} decorative />
					<p className='truncate text-[13px] leading-4.5 font-medium text-text-heading'>{tool.label}</p>
					<AccessBadge riskLevel={tool.riskLevel} />
				</div>
				<p className='text-xs leading-4 text-gray-800'>{tool.provider ? humanize(tool.provider) : 'Any integration'}</p>
			</div>
			{tool.description && (
				<p title={tool.description} className='line-clamp-4 text-xs leading-4 text-text-body'>
					{tool.description}
				</p>
			)}
			<div className='rounded-lg border border-gray-500 bg-gray-50/60 px-3 py-2 font-mono text-xs leading-6 break-words'>
				<span className='font-medium text-indigo-700'>{tool.tool}</span>
				{list(inputs, '(', ')')}
				{outputs.length > 0 && (
					<div className='pl-4'>
						<Punct>{'→ '}</Punct>
						{list(outputs, '{ ', ' }')}
					</div>
				)}
			</div>
			<Inspector slot={[...inputs, ...outputs].find((slot) => slot.id === activeId) ?? null} />
		</div>
	);
};

export const ToolDetails = ({ tool }: { tool: ToolDefinitionAction | null }) =>
	tool ? (
		<ToolCard key={toolKey(tool.provider, tool.tool)} tool={tool} />
	) : (
		<p className='p-4 text-xs leading-4 text-gray-800'>Click a tool to see what it takes and returns.</p>
	);
